import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { 
  MessageSquare, 
  X, 
  Send, 
  Loader2, 
  Sparkles,
  ChevronDown,
  ChevronRight,
  ArrowRight,
  Eye,
  ExternalLink
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { usePlan } from '../hooks/usePlan';
import { Lock } from 'lucide-react';

const HowIGotThis = ({ trace, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen(o => !o)} className="inline-flex items-center gap-1 text-[12px] font-medium text-[#6B6558] hover:text-[#1A1A1A]" data-testid={`ai-how-toggle-${index}`}>
        <ChevronRight className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-90' : ''}`} /> How I got this
      </button>
      {open && (
        <div className="mt-1.5 rounded-md border border-[#EDEAE2] bg-[#FBFAF6] px-3 py-2 text-[12px] text-[#4A463E]" data-testid={`ai-how-panel-${index}`}>
          <p className="text-[11px] text-[#8A857A] mb-1">Searched your Guardian data. Only these records were used:</p>
          <ul className="space-y-0.5">
            {trace.map((t, j) => (
              <li key={j} className="flex justify-between gap-3" data-testid={`ai-how-item-${index}-${j}`}>
                <span>{t.scope}</span>
                <span className="font-mono font-semibold whitespace-nowrap">
                  {t.shown != null && t.shown < t.count ? `Showing ${t.shown} of ${t.count}` : t.count}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

// AI Chat Panel Component
export const AIChatPanel = ({ 
  isOpen, 
  onClose, 
  contextType = 'global', 
  contextId = null,
  contextTitle = null,
  embedded = false 
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionGroups, setSuggestionGroups] = useState([]);
  const [scopeInfo, setScopeInfo] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const { token } = useAuth();
  const navigate = useNavigate();

  // Context-specific suggested prompts
  const contextSuggestions = {
    work_order: [
      "What's the history of this asset?",
      "Has this type of issue happened before?",
      "What parts might I need for this repair?",
      "Show me recent defects for this asset"
    ],
    asset: [
      "What maintenance is due on this asset?",
      "Show me the inspection history",
      "Are there any open defects?",
      "What work orders are pending?"
    ],
    defect: [
      "Has this defect type occurred before?",
      "What's the recommended fix?",
      "Show me similar defects on other assets",
      "What parts are typically needed?"
    ],
    global: []
  };

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      loadSuggestions();
    }
  }, [isOpen]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadSuggestions = async () => {
    // Use context-specific suggestions if available
    if (contextType && contextSuggestions[contextType]?.length > 0) {
      setSuggestions(contextSuggestions[contextType]);
      return;
    }
    
    // Otherwise load from API
    try {
      const res = await fetch(`${API_URL}/api/ai/suggestions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.suggestions || []);
        setSuggestionGroups(data.groups || []);
        setScopeInfo(data.scope || null);
      }
    } catch (e) {
      console.error('Failed to load suggestions');
    }
  };

  const sendMessage = async (text = null) => {
    const messageText = text || input.trim();
    if (!messageText || loading) return;

    const userMessage = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/ai/chat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          message: messageText,
          conversation_id: conversationId,
          context_type: contextType,
          context_id: contextId
        })
      });

      if (res.status === 402) {
        const errorMessage = {
          role: 'assistant',
          content: 'Guardian AI is part of the Pro plan. Upgrade to unlock fleet-wide AI insights.',
          isError: true,
          upgrade: true,
        };
        setMessages(prev => [...prev, errorMessage]);
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Guardian AI couldn\'t answer right now. Please try again.');
      }

      const data = await res.json();
      setConversationId(data.conversation_id);

      const aiMessage = {
        role: 'assistant',
        content: data.response,
        sources: data.sources,
        followups: data.suggested_followups,
        trace: data.trace,
        outcome: data.outcome,
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      const errorMessage = {
        role: 'assistant',
        content: error.message || 'Guardian AI couldn\'t answer right now. Please try again.',
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSourceClick = (source) => {
    const routes = {
      asset: '/assets',
      defect: '/defects',
      work_order: '/work-orders',
      inspection: '/inspections',
      maintenance: '/maintenance',
    };
    const route = routes[source.type];
    if (route) {
      const param = source.type === 'work_order' ? 'view' : 'id';
      navigate(`${route}?${param}=${source.id}`);
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setConversationId(null);
  };

  if (!isOpen) return null;

  // For embedded mode, render without the floating container
  const chatContent = (
    <>
      {/* Messages — its own scroll region */}
      <ScrollArea className={embedded ? "h-64" : "flex-1 min-h-0"}>
        <div className="p-4">
          {messages.length === 0 ? (
            <div className="py-2" data-testid="ai-empty-state">
              <h4 className="text-base font-semibold text-[#1A1A1A]">What do you need to know?</h4>
              <p className="text-sm text-[#8A857A] mt-1 mb-4">
                {contextType === 'work_order'
                  ? "Ask about this asset, similar issues or repair guidance."
                  : "Ask about your assets, defects, work orders and schedules."}
              </p>
              {(suggestionGroups.length > 0 ? suggestionGroups : [{ label: '', items: suggestions.slice(0, 4) }]).map((group, gi) => (
                <div key={gi} className="mb-4">
                  {group.label && <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-[#A8A296] mb-1.5">{group.label}</div>}
                  <div className="rounded-lg border border-[#EDEAE2] overflow-hidden bg-white">
                    {group.items.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => sendMessage(s)}
                        className="w-full flex items-center justify-between gap-2 text-left text-sm text-[#2A2A2A] px-3 py-2.5 hover:bg-[#FBFAF6] border-b border-[#F3F1EB] last:border-0 transition-colors"
                        data-testid={`ai-suggestion-${gi}-${i}`}
                      >
                        <span>{s}</span>
                        <ChevronRight className="w-4 h-4 text-[#C9C2B4] shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg, i) => (
                <div key={i} data-testid={`ai-message-${msg.role}`}>
                  {msg.role === 'user' ? (
                    <div className="flex justify-end">
                      <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-[#FFF7D1] border border-[#E8D48A] text-[#1A1A1A] px-3.5 py-2 text-sm">{msg.content}</div>
                    </div>
                  ) : (
                    <div className={`rounded-lg ${msg.isError ? 'bg-[#FDEDE8] border border-[#F3C7BB] p-3' : ''}`}>
                      {/* Answer as plain text on white */}
                      <div className="text-sm text-[#2A2A2A] leading-relaxed whitespace-pre-wrap">{msg.content}</div>

                      {msg.upgrade && (
                        <button onClick={() => { onClose(); navigate('/billing'); }} className="mt-2 text-sm font-semibold text-[#B45309] hover:underline">Upgrade to Pro →</button>
                      )}

                      {/* Cited records as openable rows */}
                      {msg.sources && msg.sources.length > 0 && (
                        <div className="mt-3 rounded-lg border border-[#EDEAE2] overflow-hidden bg-white">
                          {msg.sources.map((source, j) => (
                            <button
                              key={j}
                              onClick={() => handleSourceClick(source)}
                              className="w-full flex items-center gap-3 text-left px-3 py-2.5 hover:bg-[#FBFAF6] border-b border-[#F3F1EB] last:border-0 transition-colors"
                              data-testid={`ai-record-${source.type}-${source.id}`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-[#1A1A1A] truncate">{source.title || source.label}</div>
                                {source.subtitle && <div className="text-xs text-[#8A857A] truncate mt-0.5">{source.subtitle}</div>}
                              </div>
                              {source.badge && (
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wide shrink-0 ${/CRIT|HIGH|OVERDUE/.test(source.badge) ? 'bg-[#FDEDE8] text-[#B4432A]' : /MED/.test(source.badge) ? 'bg-[#FFF3C4] text-[#7A5B00]' : 'bg-[#EDEAE2] text-[#6B6558]'}`}>{source.badge}</span>
                              )}
                              <ChevronRight className="w-4 h-4 text-[#C9C2B4] shrink-0" />
                            </button>
                          ))}
                        </div>
                      )}

                      {/* How I got this */}
                      {msg.trace && msg.trace.length > 0 && <HowIGotThis trace={msg.trace} index={i} />}

                      {/* Follow-ups as quiet links */}
                      {msg.followups && msg.followups.length > 0 && (
                        <div className="mt-3 flex flex-col gap-1.5">
                          {msg.followups.map((followup, j) => (
                            <button
                              key={j}
                              onClick={() => sendMessage(followup)}
                              className="inline-flex items-center gap-1.5 text-left text-[13px] text-[#B45309] hover:text-[#7A5B00] transition-colors"
                              data-testid={`ai-followup-${j}`}
                            >
                              <ArrowRight className="w-3.5 h-3.5 shrink-0" />
                              <span>{followup}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {loading && (
                <div className="flex items-center gap-2 text-sm text-[#8A857A]"><Loader2 className="w-4 h-4 animate-spin" /> Thinking…</div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <div className={embedded ? "pt-3 border-t border-[#EDEAE2]" : "p-3 border-t border-[#EDEAE2] bg-white"}>
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={contextType === 'work_order' ? "Ask about this work order…" : "Ask about your data…"}
            disabled={loading}
            className="flex-1"
            data-testid="ai-input"
          />
          <Button onClick={() => sendMessage()} disabled={loading || !input.trim()} size={embedded ? "sm" : "default"} data-testid="ai-send">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </>
  );

  // Embedded mode: return just the content without the floating container
  if (embedded) {
    return <div className="flex flex-col">{chatContent}</div>;
  }

  // Floating mode: wrap in positioned container — four fixed bands
  return (
    <div
      className="fixed z-50 shadow-2xl rounded-xl overflow-hidden border border-[#E6E2D9] bg-[#FBFAF7] flex flex-col"
      style={{ bottom: '20px', right: '20px', width: '456px', height: 'max(520px, min(820px, 100vh - 40px))' }}
      data-testid="ai-chat-panel"
    >
      {/* Band 1: Header */}
      <div className="bg-white border-b border-[#EDEAE2] px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-full bg-[#FFF3C4] flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-[#C9A400]" />
          </div>
          <div>
            <h3 className="font-semibold text-sm text-[#1A1A1A]">Guardian AI</h3>
            {contextTitle && <p className="text-xs text-[#8A857A]">{contextTitle}</p>}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <Button variant="ghost" size="sm" onClick={clearChat} className="text-xs text-[#8A857A]" data-testid="ai-clear">
              Clear
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="ai-close">
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Band 2: Read-only scope line */}
      <div className="bg-[#F3F1EB] border-b border-[#EDEAE2] px-4 py-2 flex items-center gap-2 shrink-0" data-testid="ai-scope-line">
        <Eye className="w-3.5 h-3.5 text-[#8A857A] shrink-0" />
        <p className="text-[11px] text-[#6B6558] flex-1 leading-tight">
          Sees your {scopeInfo?.asset_count != null ? scopeInfo.asset_count : ''} assets, defects, work orders and schedules. Cannot change them.
        </p>
        <span className="px-1.5 py-0.5 rounded bg-[#E6E2D9] text-[#6B6558] text-[9px] font-bold tracking-wide shrink-0">READ ONLY</span>
      </div>

      {/* Bands 3 & 4: messages + input */}
      {chatContent}
    </div>
  );
};

// Floating AI Chat Button
export const AIChatButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { isPro, loading, onUpgrade } = usePlan();

  // While we don't yet know the plan, render nothing — avoids a flash of
  // the unlocked icon for Free users.
  if (loading) return null;

  if (!isPro) {
    return (
      <button
        onClick={onUpgrade}
        title="Guardian AI is part of Pro — click to upgrade"
        className="fixed bottom-4 right-4 z-50 w-14 h-14 rounded-full bg-secondary/80 backdrop-blur border border-primary/30 text-foreground shadow-lg hover:bg-secondary hover:border-primary/60 hover:shadow-[0_0_24px_hsl(var(--primary)/0.25)] transition-all flex items-center justify-center group"
        data-testid="ai-chat-locked-button"
      >
        <MessageSquare className="w-6 h-6 opacity-50" />
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full flex items-center justify-center border border-background">
          <Lock className="w-3 h-3 text-background" />
        </span>
        <span className="absolute right-full mr-3 whitespace-nowrap bg-popover border border-border text-foreground text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
          Guardian AI · Pro feature
        </span>
      </button>
    );
  }

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-4 right-4 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all flex items-center justify-center group"
        data-testid="ai-chat-button"
      >
        {isOpen ? (
          <ChevronDown className="w-6 h-6" />
        ) : (
          <MessageSquare className="w-6 h-6" />
        )}
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
          <Sparkles className="w-2.5 h-2.5 text-white" />
        </span>
      </button>

      <AIChatPanel isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

export default AIChatButton;
